import { Button, FileUploaderButton, InlineNotification, Modal, Tag } from '@carbon/react';
import { Download, TrashCan } from '@carbon/icons-react';
import {
  asProblem,
  downloadCsv,
  hasPermission,
  ListTable,
  problemMessage,
  RowAction,
  StatTile,
  StatTiles,
  useAdmin,
  useFormat,
} from '@pos/admin-ui';
import type { ApiProblem, HoComponents } from '@pos/api-client';
import { useTranslation, type I18nKey } from '@pos/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { hoApi } from '../api';
import { ReportFrame } from './reportFrame';
import { rangeQuery, unwrap, useReportRange } from './reportRange';

type Report = HoComponents['schemas']['ManualQrReport'];
type Row = HoComponents['schemas']['ManualQrRow'];
type Import = HoComponents['schemas']['SettlementImportDto'];
type ImportResult = HoComponents['schemas']['SettlementImportResult'];

const RECONCILE = 'ho.reconciliation.manage';

/** Carbon tag colours: what needs a look stands out, what is proven stays quiet. */
const MATCH_TAG: Record<string, 'red' | 'magenta' | 'green' | 'teal' | 'cyan'> = {
  not_found: 'red',
  amount_differs: 'magenta',
  reference: 'green',
  gateway: 'teal',
  amount_time: 'cyan',
};

const n = (value: number | string | null | undefined) => Number(value ?? 0);

/**
 * QR reconciliation (FR-RPT-03, ADR 0098): every QR, transfer and e-wallet tender stores confirmed by hand, next to what the
 * provider shows (HO's gateway payment, or a line of an uploaded settlement or bank statement), mismatches first. Accounting
 * uploads the files here; a wrong upload can be removed.
 */
export function QrReconciliationPage() {
  const { t } = useTranslation();
  const format = useFormat();
  const queryClient = useQueryClient();
  const { session } = useAdmin();
  const canUpload = hasPermission(session, RECONCILE);
  const [range, setRange] = useReportRange();
  const [uploaded, setUploaded] = useState<ImportResult>();
  const [removing, setRemoving] = useState<Import>();
  const r = (key: string, options?: Record<string, unknown>) => t(`reports.qr.${key}` as I18nKey, options);

  const report = useQuery({
    queryKey: ['reports', 'qr', range],
    queryFn: () =>
      unwrap<Report>(
        hoApi.GET('/api/v1/integration/payment/qr-reconciliation', { params: { query: rangeQuery(range) } }),
      ),
  });
  const imports = useQuery({
    queryKey: ['reports', 'qr', 'imports'],
    queryFn: () => unwrap<Import[]>(hoApi.GET('/api/v1/integration/payment/settlements')),
  });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['reports', 'qr'] });
  const upload = useMutation({
    mutationFn: async (file: File) => {
      const body = new FormData();
      body.append('file', file, file.name);
      body.append('source', 'bank');
      // openapi-fetch sends FormData as it is and lets the browser set the multipart boundary.
      const { data, error } = await hoApi.POST('/api/v1/integration/payment/settlements', { body: body as never });
      if (error) throw error as ApiProblem;
      return data as ImportResult;
    },
    onSuccess: (result) => {
      setUploaded(result);
      refresh();
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) =>
      unwrap(hoApi.DELETE('/api/v1/integration/payment/settlements/{id}', { params: { path: { id } } })),
    onSuccess: () => {
      setRemoving(undefined);
      refresh();
    },
  });

  const totals = report.data?.totals;
  const rows = (report.data?.rows ?? []).map((x) => ({ ...x, id: x.paymentId }));
  const match = (row: Row) => <Tag type={MATCH_TAG[row.match] ?? 'gray'}>{r(`match.${row.match}`)}</Tag>;
  const template = () =>
    downloadCsv('settlement-template.csv', [
      ['paid_at', 'amount', 'transaction_id', 'memo', 'store_code'],
      ['12/10/2026 12:01', 150000, 'FT0001', 'QR123456', ''],
    ]);

  return (
    <ReportFrame
      title={r('title')}
      description={r('description')}
      range={range}
      onRange={setRange}
      error={report.error}
      csvName="qr-reconciliation"
      csv={
        rows.length > 0
          ? () => [
              [
                r('status'),
                r('paidAt'),
                r('store'),
                r('method'),
                r('reference'),
                r('amount'),
                r('evidenceAmount'),
                r('evidence'),
                r('staff'),
              ],
              ...rows.map((x) => [
                r(`match.${x.match}`),
                x.paidAt,
                x.storeName,
                x.methodType,
                x.reference ?? '',
                n(x.amount),
                x.evidenceAmount == null ? null : n(x.evidenceAmount),
                x.evidence ?? '',
                x.receivedBy,
              ]),
            ]
          : undefined
      }
      actions={
        canUpload && (
          <FileUploaderButton
            labelText={r('upload')}
            buttonKind="tertiary"
            accept={['.csv', 'text/csv']}
            multiple={false}
            disableLabelChanges
            disabled={upload.isPending}
            onChange={(event) => {
              const file = (event.target as HTMLInputElement).files?.[0];
              if (file) upload.mutate(file);
            }}
          />
        )
      }
    >
      {upload.error && (
        <InlineNotification
          kind="error"
          lowContrast
          title={problemMessage(t, asProblem(upload.error))}
          onClose={() => upload.reset()}
        />
      )}
      {uploaded && (
        <InlineNotification
          kind={uploaded.rejected.length > 0 ? 'warning' : 'success'}
          lowContrast
          title={r('uploaded', {
            imported: n(uploaded.imported),
            duplicates: n(uploaded.duplicates),
            skipped: n(uploaded.skipped),
          })}
          subtitle={
            uploaded.rejected.length > 0
              ? r('rejected', {
                  lines: uploaded.rejected.map((p) => `${n(p.line)} (${r(`reason.${p.reason}`)})`).join(', '),
                })
              : undefined
          }
          onClose={() => setUploaded(undefined)}
        />
      )}
      {totals?.capped && <InlineNotification kind="info" lowContrast hideCloseButton title={r('capped')} />}

      <StatTiles>
        <StatTile
          label={r('confirmations')}
          value={format.number(n(totals?.amount))}
          caption={r('count', { count: n(totals?.count) })}
          isLoading={report.isLoading}
        />
        <StatTile
          label={r('matched')}
          value={format.number(n(totals?.matchedAmount))}
          caption={r('count', { count: n(totals?.matched) })}
          isLoading={report.isLoading}
        />
        <StatTile
          label={r('toCheck')}
          value={format.number(n(totals?.unmatchedAmount))}
          caption={r('count', { count: n(totals?.unmatched) })}
          isLoading={report.isLoading}
        />
      </StatTiles>

      <ListTable<Row & { id: string }>
        title={r('confirmationsTitle')}
        description={r('how')}
        columns={[
          { key: 'match', header: r('status'), render: match },
          { key: 'paidAt', header: r('paidAt'), render: (x) => format.dateTime(x.paidAt) },
          { key: 'storeName', header: r('store') },
          {
            key: 'methodType',
            header: r('method'),
            render: (x) => t(`md.enums.method_type.${x.methodType}` as I18nKey, { defaultValue: x.methodType }),
          },
          { key: 'reference', header: r('reference'), render: (x) => x.reference ?? '' },
          { key: 'amount', header: r('amount'), numeric: true, render: (x) => format.number(n(x.amount)) },
          {
            key: 'evidence',
            header: r('evidence'),
            render: (x) =>
              x.evidenceAmount == null
                ? ''
                : `${format.number(n(x.evidenceAmount))}${x.evidence ? ` · ${x.evidence}` : ''}${x.evidenceAt ? ` · ${format.dateTime(x.evidenceAt)}` : ''}`,
          },
          {
            key: 'receivedBy',
            header: r('staff'),
            render: (x) => `${x.receivedBy}${x.approved ? ` · ${r('approved')}` : ''}`,
          },
        ]}
        rows={rows}
        ariaLabel={r('confirmationsTitle')}
        loading={report.isLoading}
        empty={{ heading: r('none') }}
      />

      <ListTable<Import>
        title={r('uploads')}
        toolbarActions={
          <Button kind="ghost" renderIcon={Download} onClick={template}>
            {r('template')}
          </Button>
        }
        columns={[
          { key: 'fileName', header: r('file') },
          { key: 'uploadedAt', header: r('uploadedAt'), render: (x) => format.dateTime(x.uploadedAt) },
          { key: 'lineCount', header: r('lines'), numeric: true, render: (x) => format.number(n(x.lineCount)) },
          {
            key: 'duplicateCount',
            header: r('duplicates'),
            numeric: true,
            render: (x) => format.number(n(x.duplicateCount)),
          },
          ...(canUpload
            ? [
                {
                  key: 'actions',
                  header: '',
                  render: (x: Import) => (
                    <RowAction
                      action={t('admin.delete')}
                      record={x.fileName}
                      icon={TrashCan}
                      onClick={() => setRemoving(x)}
                    />
                  ),
                },
              ]
            : []),
        ]}
        rows={imports.data ?? []}
        ariaLabel={r('uploads')}
        loading={imports.isLoading}
        empty={{ heading: r('noUploads'), description: r('templateHint') }}
      />

      <Modal
        open={Boolean(removing)}
        danger
        modalHeading={r('removeTitle')}
        primaryButtonText={t('admin.delete')}
        secondaryButtonText={t('common.cancel')}
        primaryButtonDisabled={remove.isPending}
        onRequestClose={() => setRemoving(undefined)}
        onRequestSubmit={() => removing && remove.mutate(removing.id)}
      >
        <p>{r('removeBody', { file: removing?.fileName ?? '' })}</p>
      </Modal>
    </ReportFrame>
  );
}
